import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useUpdateTicket, useAssignTicket } from '@/hooks/tickets';
import { useUsers } from '@/hooks/users';
import type { Ticket, TicketStatus, TicketCategory } from './types';
import {
  STATUS_LABELS,
  CATEGORY_LABELS,
  ALL_STATUSES,
  ALL_CATEGORIES,
  UNCATEGORIZED,
  UNASSIGNED,
} from './constants';

interface Props {
  ticket: Ticket;
  canEdit: boolean;
  isAdmin: boolean;
}

function SidebarField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <p className='text-[10px] font-bold tracking-[0.12em] uppercase mb-1.5' style={{ color: 'var(--muted-foreground)' }}>
        {label}
      </p>
      {children}
    </div>
  );
}

export function TicketSidebar({ ticket, canEdit, isAdmin }: Props) {
  const ticketId = ticket.id.toString();
  const updateMutation = useUpdateTicket(ticketId);
  const assignMutation = useAssignTicket(ticketId);
  const { data: usersData } = useUsers(isAdmin);

  return (
    <div className='rounded-lg p-4 space-y-4 card-glow' style={{ background: 'var(--card)', border: '1px solid var(--border)' }}>
      <SidebarField label='Status'>
        {canEdit ? (
          <Select
            value={ticket.status}
            onValueChange={(v) => { if (v) updateMutation.mutate({ status: v as TicketStatus }); }}
          >
            <SelectTrigger className='w-full h-8 text-sm' aria-label='Update status'>
              <SelectValue>{STATUS_LABELS[ticket.status]}</SelectValue>
            </SelectTrigger>
            <SelectContent>
              {ALL_STATUSES.map((s) => (
                <SelectItem key={s} value={s}>{STATUS_LABELS[s]}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : (
          <p className='text-sm'>{STATUS_LABELS[ticket.status]}</p>
        )}
      </SidebarField>

      <SidebarField label='Category'>
        {canEdit ? (
          <Select
            value={ticket.category ?? UNCATEGORIZED}
            onValueChange={(v) => {
              if (v === null) return;
              updateMutation.mutate({ category: v === UNCATEGORIZED ? null : (v as TicketCategory) });
            }}
          >
            <SelectTrigger className='w-full h-8 text-sm' aria-label='Update category'>
              <SelectValue>
                {ticket.category ? CATEGORY_LABELS[ticket.category] : 'None'}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={UNCATEGORIZED}>None</SelectItem>
              {ALL_CATEGORIES.map((c) => (
                <SelectItem key={c} value={c}>{CATEGORY_LABELS[c]}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : (
          <p className='text-sm'>
            {ticket.category ? (
              CATEGORY_LABELS[ticket.category]
            ) : (
              <span className='text-muted-foreground'>None</span>
            )}
          </p>
        )}
      </SidebarField>

      <SidebarField label='Assigned to'>
        {isAdmin && usersData ? (
          <Select
            value={ticket.assignedAgentId ?? UNASSIGNED}
            onValueChange={(v) => {
              if (v === null) return;
              assignMutation.mutate(v === UNASSIGNED ? null : v);
            }}
          >
            <SelectTrigger className='w-full h-8 text-sm' aria-label='Assign agent'>
              <SelectValue>
                {ticket.assignedAgent?.name ?? 'Unassigned'}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={UNASSIGNED}>Unassigned</SelectItem>
              {usersData.users.map((u) => (
                <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : (
          <p className='text-sm'>
            {ticket.assignedAgent?.name ?? (
              <span className='text-muted-foreground'>Unassigned</span>
            )}
          </p>
        )}
      </SidebarField>
    </div>
  );
}
